import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserApi } from '../store/api/hooks/userApiHook'
import { decodeJWT, trackEvent } from '../_common/functions'
import { useAccount, useMsal } from '@azure/msal-react'
import { ACTIONS, APP_ENV, KEYS } from '../_config'
import { setRumUser } from '../utils/analytics/rumTracking'
import { useDispatch } from 'react-redux'
import { useFpLoader } from '../store/api/hooks/fpLoaderHook'
import { loginRequest } from '../_config/authConfig'
import { useLanguage } from '../store/api/hooks/langHook'

interface AuthProps {
    _ref: any
    onLogin: (data: any) => void
}

const Auth: React.FC<AuthProps> = ({ _ref, onLogin }) => {
    const { t } = useTranslation()
    const userApi = useUserApi()
    const { accounts, instance } = useMsal()
    const account: any = useAccount(accounts[0] || {})
    const dispatch = useDispatch()
    const { showLoader, hideLoader } = useFpLoader()
    const { setLanguage } = useLanguage()

    /* const getProfilePhoto = async () => {
        if (!account) return

        const response = await instance.acquireTokenSilent({
            scopes: ['User.Read'],
            account,
        })

        const accessToken = response.accessToken

        const photoResponse = await fetch(
            'https://graph.microsoft.com/v1.0/me/photo/$value',
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        )

        if (!photoResponse.ok) {
            throw new Error('Profile photo not available')
        }

        const blob = await photoResponse.blob()

        return URL.createObjectURL(blob)
    } */

    const login = async (type: 'login' | 'signup') => {
        let idToken: any = null
        if (account) {
            const decodedToken = decodeJWT(account.idToken)
            const currentTime = Math.floor(Date.now() / 1000) // Current time in seconds
            if (decodedToken.exp < currentTime) {
                idToken = await regenerateIdToken()
            } else {
                idToken = account.idToken
            }
        } else {
            onLogin({ type: 'error', message: t('No account found') })
            return
        }

        /*  const profilePhotoUrl = await getProfilePhoto()
         if (profilePhotoUrl) {
             globalThis.localStorage.setItem('profileImage', profilePhotoUrl)
         } */

        showLoader()
        userApi.ssoverify(
            {
                id_token: idToken,
                refresh_token: idToken,
            },
            (resp: any) => {
                if (type === 'login') {
                    if (resp?.data?.is_user_available === true) {
                        const userData = resp.data
                        if (APP_ENV === 'production' || APP_ENV === 'qa') {
                            setRumUser({
                                id: userData.user_literal || userData.email || 'unknown_user',
                                email: userData.email,
                                name: `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || undefined,
                                zones: userData.zones,
                                business_functions: userData.business_functions,
                                countries: userData.countries,
                            })
                        }
                        trackEvent('user_logged_in', {
                            first_name: userData.first_name,
                            last_name: userData.last_name,
                            email: userData.email,
                            zones: userData.zones,
                            business_functions: userData.business_functions,
                            timestamp: new Date().toISOString(),
                            countries: userData.countries,
                            user_id: userData.user_literal,
                            method: 'SSO',
                        })

                        dispatch({
                            type: ACTIONS.USER.LOGIN,
                            payload: {
                                user: resp.data,
                                accessToken: idToken,
                                refreshToken: idToken,
                                isAuthenticated: true,
                                permissions: resp.data.permissions,
                            },
                        })
                        setLanguage(resp.data.language)
                        localStorage.setItem(KEYS.AUTH_ACCESS, idToken)
                        localStorage.setItem(KEYS.AUTH_REFRESH, idToken)
                        onLogin({ type: 'success', message: t('Login successful') })
                        //navigate(URLS.MY_EXPERIMENTS)
                    } else {
                        trackEvent('auth_login_user_not_found', { email: account?.username, method: 'SSO' })
                        onLogin({ type: 'error', message: t("You don't have an account. Please Signup first.") })
                    }
                } else if (resp?.data && resp?.data?.is_user_available === true) {
                    trackEvent('auth_signup_already_registered', { email: account?.username, method: 'SSO' })
                    onLogin({ type: 'error', message: t('Your account is already registered. Please Login.') })
                } else {
                    trackEvent('signup_started', {
                        method: 'SSO',
                        email: account?.username,
                        timestamp: new Date().toISOString(),
                    })
                    onLogin({ type: 'signup', message: t('Please register to continue.') })
                }
                hideLoader()
            },
            (err: any) => {
                trackEvent('auth_sso_verify_error', { error_message: err.message })
                onLogin({ type: 'error', message: err.message })
                hideLoader()
            },
        )
    }

    const regenerateIdToken = async () => {
        try {
            trackEvent('auth_token_refresh_attempt', { method: 'silent' })
            const response = await instance.acquireTokenSilent({
                scopes: [...loginRequest.scopes, 'openid'],
                account: accounts[0],
            })

            if (response?.idToken) {
                const decodedToken = decodeJWT(response.idToken)
                const currentTime = Math.floor(Date.now() / 1000)

                if (decodedToken.exp < currentTime) {
                    return await acquireNewToken()
                }
                return response.idToken
            } else {
                return null
            }
        } catch (error) {
            console.error('Error acquiring token:', error)
            return null
        }
    }

    const acquireNewToken = async () => {
        try {
            trackEvent('auth_token_refresh_fallback', { method: 'popup' })
            const response = await instance.acquireTokenPopup({
                scopes: [...loginRequest.scopes, 'openid'],
            })
            return response.idToken
        } catch (error) {
            console.error('Error acquiring new token:', error)
            return null
        }
    }

    useEffect(() => {
        if (_ref?.current) {
            _ref.current.login = login
        }
    }, [_ref])

    return <div ref={_ref} className="hidden w-full h-0"></div>
}

export default Auth
